import type { QueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import { companyDetailQueryOptions } from '@/features/company/api/queries'
import { chartOverlaysQueryOptions } from '@/features/signals/api/overlaysQueries'
import { signalPerformanceQueryOptions } from '@/features/signals/api/queries'
import { chartHistoryHeadQueryOptions } from '@/features/trading-chart/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { NEPSE_OVERLAY_SESSIONS } from '@/screens/signals/lib/nepse-window'

/** The page's three views, `?tab=`: the twelve figures, the trades, the balance line. */
export const SIGNAL_STOCK_TABS = ['stats', 'trades', 'equity'] as const
export type SignalStockTab = (typeof SIGNAL_STOCK_TABS)[number]

/** The board the page was opened from, `?from=`: where Back goes and which sidebar row lights. */
export const SIGNAL_STOCK_SOURCES = ['live', 'daily'] as const
export type SignalStockSource = (typeof SIGNAL_STOCK_SOURCES)[number]

/**
 * "Rows per page" on the equity table, 10 first (Q6). Its own set: a stock has
 * about fifteen closed trades in two years, so the lists' 15/30/50/100 would
 * mostly be one page.
 */
export const EQUITY_PAGE_SIZES = [10, 20, 50] as const
export type EquityPageSize = (typeof EQUITY_PAGE_SIZES)[number]
export const EQUITY_DEFAULT_PAGE_SIZE: EquityPageSize = 10

const equityPageSizeSchema = z.literal(EQUITY_PAGE_SIZES)

/** A pager's plain `number` narrowed to an offered size, else `fallback` (`toSignalPageSize`'s rule). */
export const toEquityPageSize = (next: number, fallback: EquityPageSize): EquityPageSize => {
  const parsed = equityPageSizeSchema.safeParse(next)
  return parsed.success ? parsed.data : fallback
}

/**
 * Every field falls back rather than throwing, as `screens/signals/search.ts`
 * asks: a stale or hand-edited value lands on the default view.
 *
 * - `tab` — the view, statistics first.
 * - `from` — Live signals unless the Daily forecast board sent the member.
 * - `page`/`pageSize` — the equity table's client paging.
 */
export const signalStockSearchSchema = z.object({
  tab: z.enum(SIGNAL_STOCK_TABS).default('stats').catch('stats'),
  from: z.enum(SIGNAL_STOCK_SOURCES).default('live').catch('live'),
  page: z.number().int().min(1).default(1).catch(1),
  pageSize: equityPageSizeSchema.default(EQUITY_DEFAULT_PAGE_SIZE).catch(EQUITY_DEFAULT_PAGE_SIZE),
})
export type SignalStockSearch = z.infer<typeof signalStockSearchSchema>

/** `/history/`'s `q` takes at most 20 characters, so no listed symbol is longer. */
const SYMBOL_MAX_LENGTH = 20

/**
 * The path's symbol as mm-be spells it, or null for one no read could match:
 * blank, or longer than any listed symbol, so not-found before any read.
 */
export const signalSymbolOf = (raw: string): string | null => {
  const symbol = raw.trim().toUpperCase()
  return symbol.length === 0 || symbol.length > SYMBOL_MAX_LENGTH ? null : symbol
}

/**
 * The Signal page's and the Technicals page's SECONDARY reads, started before
 * each one's main read so they overlap with it: the company read that names
 * the stock in the header, and the candle chart's bars and layers.
 */
export function prefetchSignalStockFrame(queryClient: QueryClient, symbol: string): void {
  prefetchRouteData(queryClient, companyDetailQueryOptions(symbol))
  prefetchRouteData(queryClient, chartHistoryHeadQueryOptions(symbol))
  prefetchRouteData(queryClient, chartOverlaysQueryOptions(symbol, NEPSE_OVERLAY_SESSIONS))
}

/**
 * The `/market/signals/stock/$symbol` loader.
 *
 * MAIN: the stock's performance (its replayed trades, the twelve figures and
 * any open signal), whose `null` is the not-found page. SECONDARY, started
 * first: `prefetchSignalStockFrame`. An invalid symbol returns before any
 * read; the screen draws not-found.
 */
export async function loadSignalStock({
  context: { queryClient },
  params,
  preload,
}: {
  context: RouterContext
  params: { symbol: string }
  preload: boolean
}): Promise<void> {
  const symbol = signalSymbolOf(params.symbol)
  if (symbol === null) return
  if (!preload) prefetchSignalStockFrame(queryClient, symbol)
  await ensureRouteData(queryClient, signalPerformanceQueryOptions(symbol))
}
